import { Button } from '@magicschool/spellbook/components/Button';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { ProgressBar } from '@magicschool/spellbook/components/ProgressBar';
import { Skeleton } from '@magicschool/spellbook/components/Skeleton';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import { Slot as RadixSlot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { InternalFileItem } from './InternalFileItem';
import type {
  DropzoneEmptyStateProps,
  DropzoneFileListProps,
  DropzonePreviewProps,
  DropzoneProgressProps,
  DropzoneTriggerProps,
  DropzoneValidationMessageProps,
} from './types';
import { downloadFile, formatFileSize, getFileExtension, getFileKey } from './utils';

export const dropzoneRootVariants = cva(
  [
    'relative',
    'flex',
    'flex-col',
    'items-center',
    'justify-center',
    'rounded-lg',
    'transition-colors',
    'duration-200',
    'outline-none',
    '[&>[data-spellbook-name]+[data-spellbook-name]]:border-t',
    '[&>[data-spellbook-name]+[data-spellbook-name]]:border-border-default',
  ],
  {
    variants: {
      size: {
        sm: ['px-4', 'py-6', 'gap-2', 'min-h-[136px]'],
        md: ['px-6', 'py-8', 'gap-3', 'min-h-[180px]'],
        lg: ['px-8', 'py-12', 'gap-4', 'min-h-[244px]'],
      },
      status: {
        default: ['border-border-default', 'bg-surface-background-0'],
        error: ['border-error-stroke', 'bg-surface-background-0'],
      },
      disabled: {
        true: ['border-disabled-background', 'bg-disabled-background'],
        false: '',
      },
      isEmpty: {
        true: [
          'border-2',
          'border-dashed',
          'cursor-pointer',
          'hover:border-border-hover',
          'hover:bg-surface-background-2',
          'focus-visible-ring',
        ],
        false: ['border', 'border-solid', 'cursor-default', 'focus-visible-ring'],
      },
      isDragAccept: {
        true: ['border-primary-stroke'],
        false: '',
      },
      isDragReject: {
        true: ['border-error-stroke'],
        false: '',
      },
    },
    compoundVariants: [
      {
        isEmpty: false,
        className: 'min-h-0 p-0 gap-0',
      },
      {
        isDragAccept: true,
        status: 'error',
        className: 'border-primary-stroke',
      },
      {
        isEmpty: false,
        disabled: false,
        status: 'default',
        className: 'hover:border-border-default hover:bg-surface-background-0',
      },
      {
        isEmpty: false,
        disabled: false,
        status: 'error',
        className: 'hover:border-error-stroke hover:bg-surface-background-0',
      },
      { isDragAccept: true, className: 'hover:border-primary-stroke hover:bg-surface-background-2' },
      { isDragReject: true, className: 'hover:border-error-stroke hover:bg-surface-background-2' },
      {
        isEmpty: true,
        disabled: false,
        status: 'error',
        className: 'hover:border-error-stroke hover:bg-surface-background-2',
      },
      {
        disabled: true,
        isEmpty: true,
        className: 'pointer-events-none cursor-not-allowed',
      },
    ],
    defaultVariants: {
      size: 'md',
      status: 'default',
      disabled: false,
      isEmpty: true,
      isDragAccept: false,
      isDragReject: false,
    },
  },
);

/** Content shown when no files are uploaded. Customize via `text` prop or parent Dropzone's `text.emptyState`. */
export const DropzoneEmptyStateSlot = Slot<DropzoneEmptyStateProps>({
  displayName: 'Dropzone.EmptyState',
  isRenderedByDefault: true,
  renderDefault: ({
    id,
    multiple = false,
    text,
    isDragActive,
    isDragAccept,
    isDragReject,
    disabled = false,
    status = 'default',
    className,
    children,
    ...props
  }) => {
    const isError = status === 'error' && !isDragActive;

    let mode: 'disabled' | 'error' | 'accept' | 'default' = 'default';
    if (disabled) mode = 'disabled';
    else if (isDragReject || isError) mode = 'error';
    else if (isDragAccept) mode = 'accept';

    const defaultTitle = text?.title ?? (multiple ? 'Upload files' : 'Upload file');
    const defaultDescription =
      text?.description ?? (multiple ? 'Drag and drop files here, or click to browse' : 'Drag and drop a file here, or click to browse');

    // Drag-reject text overrides take priority over validation-error overrides
    const errorTitle = (isDragReject ? text?.dragRejectTitle : text?.errorTitle) ?? 'File not allowed';
    const errorDescription = (isDragReject ? text?.dragRejectDescription : text?.errorDescription) ?? defaultDescription;

    const titleByMode: Record<string, string> = { error: errorTitle, accept: text?.dragAcceptTitle ?? 'Drop to upload' };
    const title = titleByMode[mode] ?? defaultTitle;
    const description = mode === 'error' ? errorDescription : defaultDescription;
    const helperText = text?.helperText;

    const isErrorMode = mode === 'error';
    const iconName = isErrorMode ? 'alert-circle' : 'upload';

    let descriptionColor: 'disabled' | 'error' | 'default-soft' = 'default-soft';
    if (mode === 'disabled') descriptionColor = 'disabled';
    if (isErrorMode && errorDescription !== defaultDescription) descriptionColor = 'error';

    const titleId = id ? `${id}-title` : undefined;
    const descriptionId = id ? `${id}-description` : undefined;
    const helperTextId = id ? `${id}-helper-text` : undefined;

    return (
      <div data-spellbook-name="dropzone-empty-state" className={cn('flex flex-col items-center gap-3', className)} {...props}>
        <Icon name={iconName} size="xl" color={isErrorMode ? 'error' : 'disabled'} />
        <div className="flex flex-col items-center gap-1">
          <Text
            id={titleId}
            variant="body-1"
            color={mode === 'disabled' ? 'disabled' : isErrorMode ? 'error' : 'default'}
            className="font-semibold text-center"
          >
            {title}
          </Text>
          <Text id={descriptionId} color={descriptionColor} className="text-center">
            {description}
          </Text>
        </div>
        {helperText && (
          <Text id={helperTextId} variant="caption-1" color={mode === 'disabled' ? 'disabled' : 'default-soft'} className="text-center">
            {helperText}
          </Text>
        )}
        {children}
      </div>
    );
  },
});

/** Container for uploaded files. Auto-renders FileItems when no children provided. */
export const DropzoneFileListSlot = Slot<DropzoneFileListProps>({
  displayName: 'Dropzone.FileList',
  isRenderedByDefault: true,
  renderDefault: ({
    files = [],
    initialFiles = [],
    onRemove,
    onInitialFileRemove,
    onInitialFileDownload,
    onDownload,
    processingStates,
    disabled = false,
    noConfirm,
    text,
    dropzoneRef,
    className,
    children,
    ...props
  }) => {
    if (children) {
      return (
        <div data-spellbook-name="dropzone-file-list" className={cn('flex flex-col w-full', className)} {...props}>
          {children}
        </div>
      );
    }

    const hasInitialFiles = initialFiles.length > 0;
    const hasNewFiles = files.length > 0;

    if (!hasInitialFiles && !hasNewFiles) {
      return null;
    }

    return (
      <div
        data-spellbook-name="dropzone-file-list"
        className={cn('flex flex-col w-full', '[&>*+*]:border-t [&>*+*]:border-border-default', className)}
        {...props}
      >
        {initialFiles.map((initialFile, index) => {
          return (
            <InternalFileItem
              key={`initial-${index}-${initialFile.title}`}
              name={initialFile.title}
              extension={initialFile.fileType}
              description={initialFile.description}
              onDownload={onInitialFileDownload ? () => onInitialFileDownload(initialFile) : undefined}
              onRemove={onInitialFileRemove ? () => onInitialFileRemove(initialFile) : undefined}
              disabled={disabled}
              noConfirm={noConfirm}
              confirmText={text}
              dropzoneRef={dropzoneRef}
              text={text}
            />
          );
        })}
        {files.map((file, index) => {
          const processingState = processingStates?.get(file);
          const isProcessing = processingState?.status === 'pending' || processingState?.status === 'processing';
          const hasError = processingState?.status === 'error';
          const progress = processingState?.progress;
          const statusText = processingState?.statusText;

          return (
            <InternalFileItem
              key={getFileKey(file, index)}
              name={file.name}
              extension={getFileExtension(file.name)}
              description={formatFileSize(file.size)}
              onDownload={() => {
                const url = URL.createObjectURL(file);
                downloadFile(url, file.name);
                URL.revokeObjectURL(url);
                onDownload?.(file);
              }}
              onRemove={onRemove ? () => onRemove(file) : undefined}
              isProcessing={isProcessing}
              progress={progress}
              statusText={statusText}
              hasError={hasError}
              disabled={disabled}
              noConfirm={noConfirm}
              confirmText={text}
              dropzoneRef={dropzoneRef}
              text={text}
            />
          );
        })}
      </div>
    );
  },
});

/** File picker button trigger */
export const DropzoneTriggerSlot = Slot<DropzoneTriggerProps>({
  displayName: 'Dropzone.Trigger',
  isRenderedByDefault: true,
  renderDefault: ({
    disabled = false,
    asChild = false,
    open,
    isEmpty = true,
    multiple = false,
    dropzoneLabelId,
    text,
    className,
    children,
    ...props
  }) => {
    const Comp = asChild ? RadixSlot : Button;
    const defaultEmptyLabel = multiple ? 'Choose files' : 'Choose file';
    const defaultFilesLabel = multiple ? 'Add more files' : 'Replace file';
    const label = isEmpty ? (text?.label ?? defaultEmptyLabel) : (text?.labelWithFiles ?? defaultFilesLabel);
    const content = children || label;

    return (
      <div data-spellbook-name="dropzone-trigger" className={cn('mt-4', className)} {...props}>
        <Comp variant="outlined" size="md" disabled={disabled} type="button" onClick={open} aria-describedby={dropzoneLabelId}>
          {content}
        </Comp>
      </div>
    );
  },
});

/** Error message display */
export const DropzoneValidationMessageSlot = Slot<DropzoneValidationMessageProps>({
  displayName: 'Dropzone.ValidationMessage',
  isRenderedByDefault: true,
  renderDefault: ({ className, children, ...props }) => {
    if (!children) {
      return null;
    }

    return (
      <div
        data-spellbook-name="dropzone-validation-message"
        role="alert"
        className={cn('flex items-center gap-1.5 mt-2', className)}
        {...props}
      >
        <Icon name="alert-circle" size="sm" color="error" aria-hidden="true" />
        <Text variant="caption-1" element="p" color="error">
          {children}
        </Text>
      </div>
    );
  },
});

/** Aggregate progress bar for file processing. Only renders when processing is active. */
export const DropzoneProgressSlot = Slot<DropzoneProgressProps>({
  displayName: 'Dropzone.Progress',
  isRenderedByDefault: true,
  renderDefault: ({ value = 0, isProcessing = false, text, className, ...props }) => {
    if (!isProcessing) return null;

    const label = text?.label ?? 'Processing...';
    const clampedValue = Math.min(100, Math.max(0, value));

    return (
      <div data-spellbook-name="dropzone-progress" className={cn('w-full px-6 py-3 border-t border-border-default', className)} {...props}>
        <Text variant="caption-1" color="default-soft" className="mb-1.5">
          {label}
        </Text>
        <ProgressBar value={clampedValue} aria-label={label}>
          <ProgressBar.Indicator hideSpinner />
        </ProgressBar>
      </div>
    );
  },
});

const previewPaddingVariants = cva('', {
  variants: {
    size: {
      sm: 'px-4 py-3',
      md: 'px-6 py-4',
      lg: 'px-8 py-6',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/** Large preview for single image files. Separate from FileList for flexibility. */
export const DropzonePreviewSlot = Slot<DropzonePreviewProps>({
  displayName: 'Dropzone.Preview',
  isRenderedByDefault: true,
  renderDefault: ({ file, previewUrl, src, processingState, fileCount = 0, size = 'md', text, initialFileTitle, className, ...props }) => {
    const isSingleFile = fileCount === 1;

    if (!isSingleFile) return null;

    const isProcessing = processingState?.status === 'pending' || processingState?.status === 'processing';
    const hasError = processingState?.status === 'error';
    const errorMessage = text?.errorDescription ?? processingState?.error?.message;
    const isImageFile = file?.type.startsWith('image/');
    const imageSrc = src ?? previewUrl;
    const filename = file?.name ?? initialFileTitle;
    const imageAlt = text?.imageAlt ?? (filename ? `Preview of ${filename}` : 'Preview');
    const paddingClass = previewPaddingVariants({ size });

    if (isImageFile && isProcessing) {
      return (
        <div data-spellbook-name="dropzone-preview" className={cn('flex items-center justify-center', paddingClass, className)} {...props}>
          <Skeleton className="w-24 h-24 rounded-lg" />
        </div>
      );
    }

    if (isImageFile && hasError) {
      return (
        <div data-spellbook-name="dropzone-preview" className={cn('flex flex-col items-center gap-3', paddingClass, className)} {...props}>
          <Icon name="alert-circle" size="xl" color="error" />
          <div className="flex flex-col items-center gap-1">
            <Text variant="body-1" color="error" className="font-semibold text-center">
              {text?.errorTitle ?? 'File not allowed'}
            </Text>
            {errorMessage && (
              <Text color="default-soft" className="text-center">
                {errorMessage}
              </Text>
            )}
          </div>
        </div>
      );
    }

    if (hasError || !imageSrc) return null;

    return (
      <div data-spellbook-name="dropzone-preview" className={cn('flex items-center justify-center', paddingClass, className)} {...props}>
        <img src={imageSrc} alt={imageAlt} className="max-h-24 max-w-xs w-auto object-contain rounded-lg" />
      </div>
    );
  },
});

export type DropzoneRootVariants = VariantProps<typeof dropzoneRootVariants>;
